import { ButtonGroup, Button } from '@chakra-ui/core';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStoreActions, useStoreState } from '../../../../../models/hooks';
import { FollowState, FollowStateEnum } from '../types';

export interface FollowWatchSelectorProps {
  positionId: string;
  recruiterId: string;
  initialState?: FollowState;
  onChange?: (newState: FollowState) => void;
}

export const FollowWatchSelector: React.FC<FollowWatchSelectorProps> = ({
  initialState = /* istanbul ignore next */ 'none',
  positionId,
  recruiterId,
  onChange,
}) => {
  const { t } = useTranslation('moniterByRecruiters');
  const [state, setState] = useState<FollowState>(initialState);

  const updatingRecruiterId = useStoreState((s) => s.moniterByRecruiters.statusUpdatingRecruiterId);
  const isLoading = updatingRecruiterId === recruiterId;
  const isUpdating = !!updatingRecruiterId && !isLoading;

  const updatePositionStatusOfRecruiter = useStoreActions((s) => s.moniterByRecruiters.updatePositionStatusOfRecruiter);

  const handleUpdateChangeStatus = React.useCallback(
    async (status: FollowStateEnum) => {
      // istanbul ignore next
      if (recruiterId && positionId && status) {
        try {
          await updatePositionStatusOfRecruiter({
            positionId,
            recruiterId,
            status,
          });
        } catch (error) {
          console.error(error);
          throw error;
        }
      }
    },
    [recruiterId, positionId, updatePositionStatusOfRecruiter],
  );

  React.useEffect(() => {
    setState(initialState);
    // eslint-disable-next-line
  }, [initialState]);

  const toggleState = async (newState: FollowStateEnum) => {
    const isCurrentState = state === newState;
    /* istanbul ignore next */
    if (isUpdating || isCurrentState) return;
    const oldState = state;
    try {
      setState(newState);
      await handleUpdateChangeStatus(newState);
      onChange?.(newState);
    } catch (error) {
      /* istanbul ignore next */
      setState(oldState);
    }
  };

  return (
    <ButtonGroup
      isAttached
      spacing={2}
      size="xs"
      colorScheme="gray"
      variant="outline"
      data-testid="followWatchSelector"
      isDisabled={isLoading || isUpdating}
      fontWeight="normal"
    >
      <Button
        isActive={state === FollowStateEnum.Followed}
        colorScheme={state === FollowStateEnum.Followed ? 'blue' : 'gray'}
        onClick={() => toggleState(FollowStateEnum.Followed)}
        isLoading={isLoading && state === FollowStateEnum.Followed}
        data-testid="followed"
        fontWeight="normal"
      >
        {t('positionRecruiters.actions.followed')}
      </Button>

      <Button
        isActive={state === FollowStateEnum.Watched}
        colorScheme={state === FollowStateEnum.Watched ? 'blue' : 'gray'}
        onClick={() => toggleState(FollowStateEnum.Watched)}
        isLoading={isLoading && state === FollowStateEnum.Watched}
        data-testid="watched"
        fontWeight="normal"
      >
        {t('positionRecruiters.actions.watched')}
      </Button>

      <Button
        isActive={state === FollowStateEnum.None}
        colorScheme={state === FollowStateEnum.None ? 'blue' : 'gray'}
        onClick={/* istanbul ignore next */ () => toggleState(FollowStateEnum.None)}
        isLoading={isLoading && state === FollowStateEnum.None}
        data-testid="none"
        fontWeight="normal"
      >
        {t('positionRecruiters.actions.none')}
      </Button>
    </ButtonGroup>
  );
};
